import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LoginIcon from '@mui/icons-material/Login';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Button, Link, Typography } from '@mui/material';
import { defineMessage, FormattedMessage } from 'react-intl';
import { MessageScreen } from '../../components/error/MessageScreen';

const fatalErrorTitleMessage = defineMessage({
  id: 'common.fatalError.title',
  defaultMessage: 'Algo salió mal',
});

const fatalErrorBodyMessage = defineMessage({
  id: 'common.fatalError.body',
  defaultMessage:
    'Probá volver al inicio. Si el problema continúa, volvé a iniciar sesión o contactanos.',
});

const fatalErrorGoHomeMessage = defineMessage({
  id: 'common.fatalError.refreshBtn',
  defaultMessage: 'Ir al inicio',
});

const fatalErrorLoginAgainMessage = defineMessage({
  id: 'common.fatalError.loginAgainBtn',
  defaultMessage: 'Iniciar sesión',
});

/**
 * Centered fatal-error content (icon + title, copy, primary go-home + secondary login).
 * Used inside a branded screen or inline under an ErrorBoundary.
 */
export function FatalError() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 1.5,
        width: '100%',
        maxWidth: 360,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <WarningAmberRoundedIcon color="warning" fontSize="small" />
        <Typography variant="h6" color="text.primary" component="h1">
          <FormattedMessage {...fatalErrorTitleMessage} />
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        <FormattedMessage {...fatalErrorBodyMessage} />
      </Typography>
      <Box
        sx={{
          mt: 0.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {/* Native anchors: FatalError renders outside Router (e.g. Bugsnag ErrorBoundary). */}
        <Button
          component="a"
          href="/"
          variant="contained"
          color="primary"
          size="small"
          startIcon={<HomeOutlinedIcon />}
        >
          <FormattedMessage {...fatalErrorGoHomeMessage} />
        </Button>
        <Link
          href="/logout"
          underline="hover"
          color="primary"
          sx={{
            typography: 'body2',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <LoginIcon fontSize="small" />
          <FormattedMessage {...fatalErrorLoginAgainMessage} />
        </Link>
      </Box>
    </Box>
  );
}

/**
 * Full-page fatal error UI (logo + FatalError) for libs consumers without the app splash.
 * In the main app, prefer the splash-branded FatalErrorScreen under core/error.
 */
export function FatalErrorScreen() {
  return (
    <MessageScreen>
      <Box sx={{ mt: 2 }}>
        <FatalError />
      </Box>
    </MessageScreen>
  );
}
